import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, InputLabel, IconButton, Tooltip, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { useCart } from '../contexts/CartContext';
import { 
  LocalFlorist,
  Nature,
  Spa,
  Healing,
  Park,
  Search,
  Store,
  LocationOn,
  Close,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';

// Add keyframes for cursor blinking
const keyframes = `
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

// Inject the keyframes into the document
const injectGlobalStyles = () => {
  const styleEl = document.createElement('style');
  styleEl.innerHTML = keyframes;
  document.head.appendChild(styleEl);
  return () => document.head.removeChild(styleEl);
};

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const PlantCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  overflow: 'hidden',
  zIndex: 1,
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for nursery stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

const bazars = [
  { bn: 'সকল বাজার', en: 'All Bazars' },
  { bn: 'চাচকৈড় বাজার', en: 'Chachkoir Bazar' },
  { bn: 'খুবজিপুর বাজার', en: 'Khubipur Bazar' },
  { bn: 'নাজিরপুর বাজার', en: 'Nazirpur Bazar' },
  { bn: 'বিলদোহর বাজার', en: 'Bildohar Bazar' },
  { bn: 'চাটখিল বাজার', en: 'Chatkhil Bazar' },
  { bn: 'সোনাইমুড়ি বাজার', en: 'Sonaimuri Bazar' },
  { bn: 'কেন্দ্রীয় বাজার', en: 'Central Bazar' },
  { bn: 'নিউ মার্কেট', en: 'New Market' },
  { bn: 'গুলিস্তান বাজার', en: 'Gulistan Bazar' },
  { bn: 'মৌচাক বাজার', en: 'Mouchak Bazar' },
  { bn: 'রকেট বাজার', en: 'Rocket Bazar' },
  { bn: 'কাওরান বাজার', en: 'Kawran Bazar' },
  { bn: 'ফার্মগেট বাজার', en: 'Farmgate Bazar' },
  { bn: 'মিরপুর বাজার', en: 'Mirpur Bazar' },
  { bn: 'উত্তরা বাজার', en: 'Uttara Bazar' },
  { bn: 'ধানমন্ডি বাজার', en: 'Dhanmondi Bazar' },
  { bn: 'মোহাম্মদপুর বাজার', en: 'Mohammadpur Bazar' },
  { bn: 'বনানী বাজার', en: 'Banani Bazar' },
  { bn: 'গুলশান বাজার', en: 'Gulshan Bazar' }
];

// Nursery categories
const nurseryItems = [
  {
    category: { bangla: "ফুল গাছ", english: "Flowering Plants" },
    icon: <LocalFlorist />,
    items: [
      { bangla: "গোলাপ", english: "Rose", icon: <LocalFlorist /> },
      { bangla: "গাঁদা", english: "Marigold", icon: <LocalFlorist /> },
      { bangla: "চামেলি", english: "Jasmine", icon: <LocalFlorist /> },
      { bangla: "বেলী", english: "Beli", icon: <LocalFlorist /> },
      { bangla: "হিবিসকাস", english: "Hibiscus", icon: <LocalFlorist /> }
    ]
  },
  {
    category: { bangla: "ফল গাছ", english: "Fruit Trees" },
    icon: <Nature />,
    items: [
      { bangla: "আম", english: "Mango", icon: <Nature /> },
      { bangla: "কাঁঠাল", english: "Jackfruit", icon: <Nature /> },
      { bangla: "লিচু", english: "Litchi", icon: <Nature /> },
      { bangla: "আমরুদ", english: "Guava", icon: <Nature /> },
      { bangla: "পেঁপে", english: "Papaya", icon: <Nature /> }
    ]
  },
  {
    category: { bangla: "সবজি গাছ", english: "Vegetable Plants" },
    icon: <Spa />,
    items: [
      { bangla: "লাউ", english: "Bottle Gourd", icon: <Spa /> },
      { bangla: "পুঁই শাক", english: "Malabar Spinach", icon: <Spa /> },
      { bangla: "টমেটো", english: "Tomato", icon: <Spa /> },
      { bangla: "বেগুন", english: "Brinjal", icon: <Spa /> },
      { bangla: "ঢেঁড়শ", english: "Okra", icon: <Spa /> }
    ]
  },
  {
    category: { bangla: "ওষধি গাছ", english: "Medicinal Plants" },
    icon: <Healing />,
    items: [
      { bangla: "তুলসী", english: "Basil", icon: <Healing /> },
      { bangla: "আলোভেরা", english: "Aloe Vera", icon: <Healing /> },
      { bangla: "নিম", english: "Neem", icon: <Healing /> },
      { bangla: "হলুদ গাছ", english: "Turmeric Plant", icon: <Healing /> },
      { bangla: "অশ্বগন্ধা", english: "Ashwagandha", icon: <Healing /> }
    ]
  },
  {
    category: { bangla: "শোভাময় গাছ", english: "Ornamental Plants" },
    icon: <Park />,
    items: [
      { bangla: "মানিপ্লান্ট", english: "Money Plant", icon: <Park /> },
      { bangla: "ক্রোটন", english: "Croton", icon: <Park /> },
      { bangla: "অর্কিড", english: "Orchid", icon: <Park /> },
      { bangla: "স্নেক প্ল্যান্ট", english: "Snake Plant", icon: <Park /> },
      { bangla: "বনসাই", english: "Bonsai", icon: <Park /> }
    ]
  }
];

// List of nursery stores
const stores = [
  {
    name: 'গ্রিন গার্ডেন',
    logo: '/images/logos/green-garden.png',
    description: 'সেরা মানের গাছ ও বাগান সামগ্রী'
  },
  {
    name: 'ফ্লাওয়ার ভ্যালি',
    logo: '/images/logos/flower-valley.png',
    description: 'ফুলের গাছের বিশেষজ্ঞ'
  },
  {
    name: 'ইকো নার্সারি',
    logo: '/images/logos/eco-nursery.png',
    description: 'পরিবেশবান্ধব গাছপালা'
  },
  {
    name: 'হার্বাল গার্ডেন',
    logo: '/images/logos/herbal-garden.png',
    description: 'ওষধি গাছের বিশেষজ্ঞ'
  },
  {
    name: 'রয়েল নার্সারি',
    logo: '/images/logos/royal-nursery.png',
    description: 'উন্নত মানের গাছ ও সেবা'
  },
  {
    name: 'বনসাই হাউস',
    logo: '/images/logos/bonsai-house.png',
    description: 'বনসাই ও শোভাময় গাছের বিশেষজ্ঞ'
  }
];

// Sample plants data
const plantsData = [
  {
    id: 1,
    name: 'গোলাপ গাছ',
    englishName: 'Rose Plant',
    category: 'গোলাপ',
    store: 'গ্রিন গার্ডেন',
    price: '৳১৫০.০০',
    image: '/images/nursery/golap.avif',
    description: 'সুগন্ধি লাল গোলাপের গাছ',
    variety: 'লাল গোলাপ'
  },
  {
    id: 2,
    name: 'আম গাছ',
    englishName: 'Mango Tree',
    category: 'আম',
    store: 'ফ্লাওয়ার ভ্যালি',
    price: '৳৮৫০.০০',
    image: '/images/nursery/amgach.jpg',
    description: 'হিমসাগর জাতের আম গাছ',
    variety: 'হিমসাগর'
  },
  {
    id: 3,
    name: 'তুলসী গাছ',
    englishName: 'Basil Plant',
    category: 'তুলসী',
    store: 'হার্বাল গার্ডেন',
    price: '৳৮০.০০',
    image: '/images/nursery/tulshi.jpeg',
    description: 'পবিত্র তুলসী গাছ',
    variety: 'শ্বেত তুলসী'
  },
  {
    id: 4,
    name: 'টমেটো চারা',
    englishName: 'Tomato Seedling',
    category: 'টমেটো',
    store: 'ইকো নার্সারি',
    price: '৳২৫.০০',
    image: '/images/nursery/tomato.jpg',
    description: 'উন্নত জাতের টমেটো চারা',
    variety: 'হাইব্রিড'
  },
  {
    id: 5,
    name: 'মানিপ্লান্ট',
    englishName: 'Money Plant',
    category: 'মানিপ্লান্ট',
    store: 'রয়েল নার্সারি',
    price: '৳১২০.০০',
    image: '/images/nursery/moneyplant.jpg',
    description: 'ঘর সাজানোর জন্য আদর্শ',
    variety: 'গোল্ডেন'
  },
  {
    id: 6,
    name: 'বনসাই গাছ',
    englishName: 'Bonsai Tree',
    category: 'বনসাই',
    store: 'বনসাই হাউস',
    price: '৳১৫০০.০০',
    image: '/images/nursery/bonsai.jpg',
    description: 'সুন্দর বনসাই গাছ',
    variety: 'ফিকাস'
  }
];

const NurseryBazar = () => {
  const { addToCart } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [typingText, setTypingText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndex, setTypingIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('সকল জেলা');
  const [selectedUpazilla, setSelectedUpazilla] = useState('সকল উপজেলা');
  const [selectedBazar, setSelectedBazar] = useState('সকল বাজার');
  const [districtSearchQuery, setDistrictSearchQuery] = useState('');
  const [upazillaSearchQuery, setUpazillaSearchQuery] = useState('');
  const [bazarSearchQuery, setBazarSearchQuery] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(150);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Typing animation effect
  const placeholderTexts = [
    'গাছের নাম লিখুন...',
    'ফুলের নাম লিখুন...',
    'নার্সারির নাম লিখুন...',
    'ক্যাটেগরির নাম লিখুন...',
    'গাছের ধরন লিখুন...'
  ];

  // Inject keyframes for the blinking cursor animation
  useEffect(() => {
    const cleanup = injectGlobalStyles();
    return cleanup;
  }, []);

  // Typewriter effect for search placeholders
  useEffect(() => {
    const currentPlaceholder = placeholderTexts[placeholderIndex];
    
    const handleTyping = () => {
      if (isDeleting) {
        setTypingText(prev => prev.slice(0, -1));
        setTypingSpeed(50);
        
        if (typingText === '') {
          setIsDeleting(false);
          setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
          setTypingSpeed(150);
        }
      } else {
        setTypingText(currentPlaceholder.slice(0, typingText.length + 1));
        setTypingSpeed(150);
        
        if (typingText === currentPlaceholder) {
          setTypingSpeed(2000);
          setIsDeleting(true);
        }
      }
    };
    
    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [typingText, isDeleting, placeholderIndex, placeholderTexts, typingSpeed]);

  const handleCategoryClick = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSubCategoryClick = (mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  };

  const handleStoreClick = (store) => {
    setSelectedStore(store.name);
  };

  const handleDistrictChange = (event) => {
    setSelectedDistrict(event.target.value);
  };

  const handleUpazillaChange = (event) => {
    setSelectedUpazilla(event.target.value);
  };

  const handleBazarChange = (event) => {
    setSelectedBazar(event.target.value);
  };

  const handleAddToCart = (product) => {
    console.log('NurseryBazar - Adding product to cart:', product);
    
    const cartProduct = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10
    };
    
    addToCart(cartProduct, 1);
  };

  const filteredPlants = plantsData.filter((plant) => {
    const matchesCategory = !selectedCategory || 
      (selectedSubCategory ? plant.category === selectedSubCategory : 
       nurseryItems.find(cat => cat.category.bangla === selectedCategory)?.items.some(item => item.bangla === plant.category));
    const matchesStore = !selectedStore || plant.store === selectedStore;
    const matchesSearch = !searchQuery || 
      plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plant.englishName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      plant.variety.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesStore && matchesSearch;
  });

  // Filter bazars based on search query
  const filteredBazars = bazars.filter(
    bazar => bazar.bn.toLowerCase().includes(bazarSearchQuery.toLowerCase())
  );

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/default.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              // Optional: Handle location change if needed
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={typingText}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: 2, 
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              },
              zIndex: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ 
                    opacity: isDeleting ? 0 : 1, 
                    transition: 'opacity 0.3s',
                    height: '1.2em', 
                    width: '0.15em', 
                    backgroundColor: 'primary.main',
                    display: 'inline-block',
                    animation: 'blink 1s step-end infinite'
                  }}/>
                </InputAdornment>
              )
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <LocalFlorist sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    গাছের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {nurseryItems.map((categoryData) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    নার্সারির তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <LocalFlorist sx={{ fontSize: 12, color: '#5e35b1' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={store.description}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Plants Grid */}
              {filteredPlants.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredPlants.map((plant) => (
                    <Box key={plant.id} sx={{ display: 'flex', width: '100%' }}>
                      <PlantCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="160"
                            image={plant.image}
                            alt={plant.name}
                            sx={{ objectFit: 'cover', backgroundColor: '#f5f5f5', pt: 1 }}
                            onError={(e) => {
                              e.target.src = '/images/placeholder.png';
                            }}
                          />
                          <StyledBadge>{plant.category}</StyledBadge>
                        </Box>
                        <CardContent sx={{
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          pb: '16px !important' // Override default padding bottom
                        }}>
                          <Box>
                            <Typography gutterBottom variant="h6" component="div">
                              {plant.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {plant.englishName}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.primary">
                                <strong>জাত:</strong> {plant.variety}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                <strong>নার্সারি:</strong> {plant.store}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{
                            mt: 'auto', // Push this box to the bottom
                            pt: 2 // Add some spacing from the content above
                          }}>
                            <Typography variant="h6" color="primary.main" sx={{ mb: 1, textAlign: 'center' }}>
                              {plant.price}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1
                            }}>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                fullWidth
                              >
                                কিনুন
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                fullWidth
                                onClick={() => handleAddToCart(plant)}
                              >
                                ব্যাগে নিন
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </PlantCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                  <LocalFlorist sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    কোন গাছ পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অনুগ্রহ করে আপনার ফিল্টার বা সার্চ পরিবর্তন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore('');
                      setSearchQuery('');
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Paper>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default NurseryBazar;
